/* Public, server-rendered batch data only. No order lookup, fetch, timers or polling. */
(() => {
  if (customElements.get('ct-preorder-tracker')) return;
  class CTPreorderTracker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.batches = [...this.querySelectorAll('[data-preorder-batch]')];
      this.productSelect = this.querySelector('[data-tracker-product]');
      this.batchSelect = this.querySelector('[data-tracker-batch]');
      this.batchField = this.querySelector('[data-tracker-batch-field]');
      this.historyInput = this.querySelector('[data-tracker-history]');
      this.message = this.querySelector('[data-tracker-message]');
      this.announcement = this.querySelector('[data-tracker-announcement]');
      this.products = new Map();
      this.batches.forEach(batch => {
        const handle = batch.dataset.productHandle;
        if (!this.products.has(handle)) this.products.set(handle, { title: batch.dataset.productTitle, batches: [] });
        this.products.get(handle).batches.push(batch);
      });
      const active = [], completed = [];
      this.products.forEach((product, handle) => {
        (product.batches.some(batch => batch.dataset.completed !== 'true') ? active : completed).push([handle, product.title]);
      });
      this.productSelect.replaceChildren(new Option('Choose your product', ''));
      for (const [name, products] of [['Active preorders', active], ['Completed batches', completed]]) {
        if (!products.length) continue;
        const group = document.createElement('optgroup'); group.label = name;
        products.forEach(([handle, title]) => group.append(new Option(title, handle)));
        this.productSelect.append(group);
      }
      this.querySelector('[data-tracker-controls]').hidden = this.batches.length === 0;
      this.querySelector('[data-tracker-history-field]').hidden = !this.batches.some(batch => batch.dataset.completed === 'true');
      const signal = this.controller.signal;
      this.productSelect.addEventListener('change', () => {
        const batches = this.products.get(this.productSelect.value)?.batches || [];
        if (batches.length && batches.every(batch => batch.dataset.completed === 'true')) this.historyInput.checked = true;
        this.selectBatch(); this.updateURL();
      }, { signal });
      this.batchSelect.addEventListener('change', () => { this.showBatch(); this.updateURL(); }, { signal });
      this.historyInput.addEventListener('change', () => {
        if (!this.productSelect.value) this.autoSelectProduct();
        this.selectBatch(this.batchSelect.value); this.updateURL();
      }, { signal });
      window.addEventListener('popstate', () => this.readURL(), { signal });
      this.dataset.enhanced = 'true';
      this.readURL();
    }
    inScope(batch) { return this.historyInput.checked || batch.dataset.completed !== 'true'; }
    autoSelectProduct() {
      const products = [...this.products].filter(([, product]) => product.batches.some(batch => this.inScope(batch)));
      this.productSelect.value = products.length === 1 ? products[0][0] : '';
    }
    readURL() {
      const params = new URL(location.href).searchParams;
      const requestedProduct = params.get('product'), requestedBatch = params.get('batch');
      this.historyInput.checked = params.get('history') === '1';
      const batch = this.batches.find(b => b.dataset.batchHandle === requestedBatch && (!requestedProduct || b.dataset.productHandle === requestedProduct));
      if (batch?.dataset.completed === 'true') this.historyInput.checked = true;
      if (batch) this.productSelect.value = batch.dataset.productHandle;
      else if (this.products.has(requestedProduct)) {
        this.productSelect.value = requestedProduct;
        if (this.products.get(requestedProduct).batches.every(b => b.dataset.completed === 'true')) this.historyInput.checked = true;
      } else this.autoSelectProduct();
      this.selectBatch(batch?.dataset.batchHandle);
    }
    selectBatch(preferred) {
      const all = this.products.get(this.productSelect.value)?.batches || [];
      const eligible = all.filter(batch => this.inScope(batch));
      this.batchSelect.replaceChildren(new Option('Choose your batch', ''));
      eligible.forEach(batch => this.batchSelect.append(new Option(batch.dataset.batchLabel + (batch.dataset.completed === 'true' ? ' — Completed' : ''), batch.dataset.batchHandle)));
      this.batchField.hidden = eligible.length < 2;
      this.batchSelect.value = eligible.some(batch => batch.dataset.batchHandle === preferred) ? preferred : eligible.length === 1 ? eligible[0].dataset.batchHandle : '';
      this.showBatch();
    }
    showBatch() {
      const selected = this.batches.find(batch => batch.dataset.productHandle === this.productSelect.value && batch.dataset.batchHandle === this.batchSelect.value && this.inScope(batch));
      this.batches.forEach(batch => { batch.hidden = batch !== selected; batch.open = batch === selected; });
      this.message.hidden = Boolean(selected);
      if (selected) {
        this.announcement.textContent = selected.dataset.productTitle + ', ' + selected.dataset.batchLabel + '. ' + selected.dataset.currentStage + '.';
      } else {
        const product = this.products.get(this.productSelect.value);
        const available = product?.batches.filter(batch => this.inScope(batch)) || [];
        if (product && !available.length) this.message.textContent = this.historyInput.checked ? 'There are no available batches for this product.' : 'There are no active preorder batches for this product. You can include completed batches above.';
        else if (product) this.message.textContent = 'Choose your batch to view its progress.';
        else if (!this.batches.some(batch => this.inScope(batch))) this.message.textContent = 'There are currently no active preorder batches.';
        else this.message.textContent = 'Choose your product to view preorder progress.';
        this.announcement.textContent = this.message.textContent;
      }
    }
    updateURL() {
      const url = new URL(location.href);
      for (const [key, value] of [['product', this.productSelect.value], ['batch', this.batchSelect.value], ['history', this.historyInput.checked ? '1' : '']]) {
        if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
      }
      // Keep Shopify preview/locale parameters and avoid trapping Back in selector changes.
      history.replaceState(history.state, '', url);
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  customElements.define('ct-preorder-tracker', CTPreorderTracker);
})();

